
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { TrialEligibilityBanner } from './TrialEligibilityBanner';
import { validateFCANumber, compareFirmNames, formatFCANumber } from '@/utils/fcaValidation';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { OfficeLocationCombobox } from '@/components/office/OfficeLocationCombobox';

// Define schema for company registration
const companySchema = z.object({
  firmType: z.enum(['fca_regulated', 'outsourced_paraplanner', 'other'], {
    required_error: "Please select a firm type",
  }),
  companyName: z.string().min(2, "Company name is required"),
  fcaNumber: z.string().optional().refine(
    (val) => !val || /^\d{6}$/.test(val),
    "FCA number must be exactly 6 digits if provided"
  ),
  officeName: z.string().min(2, "Office location name is required"),
  officeLocationId: z.string().uuid().optional(),
  jobRole: z.string().min(1, "Please select your job role"),
  contactNumber: z.string().min(5, "Contact phone is required"),
  contactAddress: z.string().optional(),
  justification: z.string().optional(),
  joinCompanyId: z.string().uuid().optional(),
  inviteToken: z.string().uuid().optional(),
  }).superRefine((data, ctx) => {
    // Skip FCA / justification rules when joining an existing company
    if (data.joinCompanyId || data.inviteToken) return;
    // If FCA regulated, FCA number is required
    if (data.firmType === 'fca_regulated' && !data.fcaNumber) {
      ctx.addIssue({
        code: 'custom',
        message: 'FCA number is required for FCA regulated firms',
        path: ['fcaNumber'],
      });
    }
    // If 'other' firm type, justification is required
    if (data.firmType === 'other' && (!data.justification || data.justification.trim().length < 20)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please provide at least 20 characters explaining why you need access',
        path: ['justification'],
      });
    }
  });

export type CompanyFormData = z.infer<typeof companySchema>;

interface CompanyRegistrationFormProps {
  onSubmit: (data: CompanyFormData) => void;
  onBack: () => void;
  isLoading: boolean;
  lockedCompany?: { id: string; name: string; jobRole?: string | null } | null;
  inviteToken?: string;
}

export function CompanyRegistrationForm({ 
  onSubmit, 
  onBack,
  isLoading,
  lockedCompany = null,
  inviteToken,
}: CompanyRegistrationFormProps) {
  const [trialEligibility, setTrialEligibility] = useState<any>(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [fcaValidation, setFcaValidation] = useState<any>(null);
  const [validatingFca, setValidatingFca] = useState(false);
  const [nameWarning, setNameWarning] = useState<string | null>(null);

  // Join-code state (used when no inviteToken / lockedCompany)
  const [joinCodeInput, setJoinCodeInput] = useState("");
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [joinedViaCode, setJoinedViaCode] = useState<{ id: string; name: string } | null>(null);

  // Effective "linked to existing company" state
  const linkedCompany = lockedCompany
    ? { id: lockedCompany.id, name: lockedCompany.name }
    : joinedViaCode;
  
  // Create a completely new form instance with no connection to any other form
  const form = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      firmType: 'fca_regulated',
      companyName: lockedCompany?.name || "",
      fcaNumber: "",
      officeName: "",
      officeLocationId: undefined,
      jobRole: lockedCompany?.jobRole || "",
      contactNumber: "",
      contactAddress: "",
      justification: "",
      joinCompanyId: lockedCompany?.id,
      inviteToken,
    },
  });

  const fcaNumber = form.watch('fcaNumber');
  const companyName = form.watch('companyName');
  const firmType = form.watch('firmType');

  // Validate FCA number and check trial eligibility when FCA number changes
  useEffect(() => {
    const validateAndCheck = async () => {
      if (linkedCompany) return; // Skip when joining existing company
      // Only validate if FCA regulated and FCA number is provided
      if (firmType === 'fca_regulated' && fcaNumber && /^\d{6}$/.test(fcaNumber)) {
        setValidatingFca(true);
        setCheckingEligibility(true);

        // Validate FCA number against register
        console.log('[CompanyReg] Validating FCA number:', fcaNumber);
        const fcaResult = await validateFCANumber(fcaNumber);
        console.log('[CompanyReg] Validation result:', fcaResult);
        setFcaValidation(fcaResult);

        // If FCA is valid and we have a firm name, pre-fill company name
        if (fcaResult.isValid && fcaResult.firmName && !companyName) {
          form.setValue('companyName', fcaResult.firmName);
        }

        // Check trial eligibility
        const { data: eligibilityData, error } = await supabase.functions.invoke(
          'check-trial-eligibility',
          { body: { fcaNumber } }
        );

        if (!error && eligibilityData) {
          setTrialEligibility(eligibilityData);
        }

        setValidatingFca(false);
        setCheckingEligibility(false);
      } else {
        setFcaValidation(null);
        setTrialEligibility(null);
        setNameWarning(null);
      }
    };

    const debounce = setTimeout(validateAndCheck, 500);
    return () => clearTimeout(debounce);
  }, [fcaNumber, firmType]);

  // Check firm name similarity when company name changes - check against ALL trading names
  useEffect(() => {
    if (fcaValidation?.isValid && fcaValidation?.tradingNames && companyName) {
      // Find the best match among all trading names
      let bestMatch = null;
      let bestScore = 0;
      
      for (const tradingName of fcaValidation.tradingNames) {
        const match = compareFirmNames(companyName, tradingName);
        if (match.similarity > bestScore) {
          bestScore = match.similarity;
          bestMatch = match;
        }
      }
      
      if (bestMatch?.shouldWarn) {
        setNameWarning(
          `This name doesn't closely match the FCA registered names. Best match: ${Math.round(bestScore)}% similarity.`
        );
      } else {
        setNameWarning(null);
      }
    } else {
      setNameWarning(null);
    }
  }, [companyName, fcaValidation]);

  const isFcaRegulated = firmType === 'fca_regulated';
  const isLinked = !!linkedCompany;
  const canSubmit = isLinked ? true : (isFcaRegulated ? (fcaValidation?.isValid && !validatingFca) : true);

  const verifyJoinCode = async () => {
    const code = joinCodeInput.trim().toUpperCase();
    if (!code) return;
    setVerifyingCode(true);
    try {
      const { data, error } = await supabase.rpc('lookup_company_by_join_code', { p_code: code });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.company_id) {
        toastInvalid();
        return;
      }
      setJoinedViaCode({ id: row.company_id, name: row.company_name });
      form.setValue('joinCompanyId', row.company_id);
      form.setValue('companyName', row.company_name);
    } catch (e) {
      toastInvalid();
    } finally {
      setVerifyingCode(false);
    }
  };

  const clearJoinedCompany = () => {
    setJoinedViaCode(null);
    setJoinCodeInput('');
    form.setValue('joinCompanyId', undefined);
    if (!lockedCompany) form.setValue('companyName', '');
  };

  const toastInvalid = () => {
    import('sonner').then(({ toast }) => toast.error('Join code not recognised'));
  };


  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Linked-company banner (invite or join code) */}
        {isLinked && (
          <Alert className="border-primary/50 bg-primary/5">
            <AlertDescription className="text-sm">
              <strong>Joining {linkedCompany!.name}</strong>
              <p className="mt-1 text-xs text-muted-foreground">
                Your account will be linked to this company. The company administrator will approve your access.
                {!lockedCompany && (
                  <button type="button" onClick={clearJoinedCompany} className="ml-2 underline">
                    Not this company?
                  </button>
                )}
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Join-code input (only when not already linked and no invite token) */}
        {!isLinked && !inviteToken && (
          <div className="rounded-md border p-3 bg-muted/20 space-y-2">
            <div className="text-sm font-medium">Joining an existing business?</div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter join code (e.g. ABCD-1234)"
                value={joinCodeInput}
                onChange={(e) => setJoinCodeInput(e.target.value.toUpperCase())}
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={verifyJoinCode} disabled={verifyingCode || !joinCodeInput.trim()}>
                {verifyingCode ? "Checking..." : "Verify"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Ask your company administrator for the code (Company Management → Invite teammates).
            </p>
          </div>
        )}

        {!isLinked && (
        <FormField
          control={form.control}
          name="firmType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Firm Type <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select firm type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fca_regulated">FCA Regulated Firm</SelectItem>
                    <SelectItem value="outsourced_paraplanner">Outsourced Paraplanner (Non-FCA)</SelectItem>
                    <SelectItem value="other">Other (Manual Review Required)</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        )}

        {!isLinked && isFcaRegulated && (
          <FormField
            control={form.control}
            name="fcaNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>FCA Number</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  maxLength={6}
                  placeholder="123456"
                  onChange={(e) => {
                    const formatted = formatFCANumber(e.target.value);
                    field.onChange(formatted);
                  }}
                />
              </FormControl>
              <FormMessage />
              {validatingFca && (
                <p className="text-sm text-muted-foreground">Validating FCA number...</p>
              )}
              {fcaValidation?.isValid === true && fcaValidation?.firmName && (
                <div className="text-sm text-green-600">
                  <p>✓ FCA number verified: {fcaValidation.firmName}</p>
                  {fcaValidation.tradingNames && fcaValidation.tradingNames.length > 1 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {fcaValidation.tradingNames.length - 1} additional trading name(s) found
                    </p>
                  )}
                </div>
              )}
              {fcaValidation?.error && (
                <p className="text-sm text-red-600">{fcaValidation.error}</p>
              )}
            </FormItem>
          )}
        />
        )}

        {/* Show trial information banner for ALL firm types */}
        {!isLinked && firmType === 'fca_regulated' && fcaValidation?.isValid && fcaValidation?.firmName && trialEligibility && !checkingEligibility && (
          <TrialEligibilityBanner 
            eligibility={trialEligibility}
            fcaNumber={fcaNumber}
          />
        )}
        
        {!isLinked && firmType === 'outsourced_paraplanner' && (
          <Alert className="border-primary/50 bg-primary/5">
            <AlertTriangle className="h-4 w-4 text-primary" />
            <AlertDescription className="text-sm">
              <strong>Outsourced Paraplanner Registration:</strong>
              <ul className="mt-2 space-y-1 text-xs">
                <li>✓ Your registration requires manual approval within 2 business days</li>
                <li>✓ You'll be asked to add payment details after submitting</li>
                <li>✓ Your 30-day free trial starts immediately after payment setup</li>
                <li>✓ You won't be charged until the trial ends</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}
        
        {!isLinked && firmType === 'other' && (
          <Alert className="border-orange-500 bg-orange-50">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800 text-sm">
              <strong>Manual Review Required:</strong>
              <ul className="mt-2 space-y-1 text-xs">
                <li>✓ Your registration will be reviewed by our team</li>
                <li>✓ You'll be contacted within 2 business days</li>
                <li>✓ Payment details required after submitting</li>
                <li>✓ 30-day trial starts after approval and payment setup</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {!isLinked && firmType === 'other' && (
          <FormField
            control={form.control}
            name="justification"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Justification for Access</FormLabel>
                <FormControl>
                  <Textarea 
                    {...field} 
                    className="min-h-[120px]" 
                    placeholder="Please explain your role, why you need access to Comparare, and how you plan to use the platform (minimum 20 characters)"
                  />
                </FormControl>
                <FormMessage />
                <p className="text-xs text-muted-foreground">
                  This information helps us verify your identity and ensure the platform is used appropriately.
                </p>
              </FormItem>
            )}
          />
        )}
        
        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company Name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter your firm's registered name" />
              </FormControl>
              <FormMessage />
              {nameWarning && (
                <Alert className="mt-2 border-yellow-500 bg-yellow-50">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800 text-sm">
                    {nameWarning}
                  </AlertDescription>
                </Alert>
              )}
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="officeName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Office Location <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <OfficeLocationCombobox
                  companyId={linkedCompany?.id ?? null}
                  useAnon
                  value={field.value ? { id: form.getValues('officeLocationId'), name: field.value } : null}
                  onChange={(sel) => {
                    field.onChange(sel?.name ?? "");
                    form.setValue('officeLocationId', sel?.id ?? undefined, { shouldValidate: true });
                  }}
                />
              </FormControl>
              <FormMessage />
              {!linkedCompany && (
                <p className="text-xs text-muted-foreground">
                  You're creating a new company, so this will become your first office.
                </p>
              )}
            </FormItem>
          )}
        />

        
        <FormField
          control={form.control}
          name="jobRole"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Job Role <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Principle">Principle</SelectItem>
                    <SelectItem value="Adviser">Adviser</SelectItem>
                    <SelectItem value="Paraplanner">Paraplanner</SelectItem>
                    <SelectItem value="Administrator">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="contactNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contact Phone <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input {...field} type="tel" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="contactAddress"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contact Address</FormLabel>
              <FormControl>
                <Textarea 
                  {...field} 
                  className="min-h-[100px]" 
                  placeholder="Enter your full address" 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="flex space-x-3">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onBack} 
            className="flex-1"
          >
            Back
          </Button>
          <Button 
            type="submit" 
            className="flex-1"
            disabled={isLoading || (isFcaRegulated && !canSubmit)}
          >
            {isLoading ? "Submitting..." : isFcaRegulated ? "Complete Registration" : "Submit for Review"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
